import React from 'react';
import { FileUpload } from '@emorg-prd/standard-react';
import AWSPortOpeningRule from '../../../../models/entities/AWSPortOpeningRule';
import { PortOpeningRequestsDirection } from '../../../../models/enums/POR/PortOpeningRequestsDirection';
import AWSPortOpeningRequestsService from '../../../../services/AWSPortOpeningRequestsService';
import CSVGeneratorButton from '../../../common/CSVGeneratorButton';
import { RuleColumnsLayout, PORColumn } from '../RuleColumnsLayout';
import { VirtualPrivateCloud } from '../../../../models/entities/AzureResources/VirtualPrivateCloud';
import StandardModal from '../../../common/modals/StandardModal';
import { Button, ButtonGroup, Icon } from '../../../common';
import { Alert } from '../../../common';

interface BulkUploadModalProps {
    direction: PortOpeningRequestsDirection;
    virtualPrivateClouds: VirtualPrivateCloud[];
    onSuccess: (rules: AWSPortOpeningRule[]) => any;
    onCancel: (...args: any[]) => any;
    visible: boolean;
}

interface BulkUploadModalState {
    loading: boolean;
    file: any;
    fileKey: number;
    alert: {
        visible: boolean;
        text?: string;
    },
    templateColumns: PORColumn[];
}

export default class BulkUploadModal
    extends React.Component<BulkUploadModalProps, BulkUploadModalState> {
    
    awsPortOpeningRequestsService: AWSPortOpeningRequestsService;

    constructor(props: BulkUploadModalProps) {
        super(props);
        this.awsPortOpeningRequestsService = new AWSPortOpeningRequestsService();
        this.state = {
            loading: false,
            file: null,
            fileKey: 0,
            alert: {
                visible: false,
            },
            templateColumns: []
        };
    }

    componentDidMount() {
        this.setColumns();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.direction?.value !== this.props.direction?.value) {
            this.setColumns();
        }
    }

    handleChange = (file) => {
        this.setState({
            file: file[0],
            alert: {
                visible: false,
            }
        });
    };

    handleUpload = async () => {
        try{
            this.setState({
                loading: true
            });
            await this.awsPortOpeningRequestsService.parse(this.props.direction, this.state.file, this.props.virtualPrivateClouds)
            .then((response: AWSPortOpeningRule[]) => {
                const rules = response.map(r => new AWSPortOpeningRule(r));
                this.props.onSuccess(rules);
            })
        }catch(err){
            this.handleFileRemoval();
            this.setState({
                alert: {
                    visible: true,
                    text: err.message
                }
            });
        }finally{
            this.setState({
                loading: false,
                file: null,
                templateColumns: [],					
            });
        }
    };

    handleCancel = () => {
        this.setState({
            loading: false,
            file: null,
            templateColumns: []
        });
        this.props.onCancel();
    }

    handleFileRemoval = () => {
        this.setState((prev) => ({
            file: null,
            fileKey: prev.fileKey + 1,
            alert: { visible: false },
        }));
    }

    setColumns = () => {
        this.setState({
            templateColumns: RuleColumnsLayout.get(this.props.direction)
        });
    };

    render() {
        return (
            <StandardModal
                show={this.props.visible}
                title="Import rules to the request"
                onDismiss={this.handleCancel}
                footer={
                    <ButtonGroup>
                        <Button
                            variant="primary"
                            onClick={this.handleUpload}
                            isLoading={this.state.loading}
                            disabled={(!this.state.file) || this.state.alert.visible}
                        >
                            Upload Rules
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={this.handleFileRemoval}
                            isLoading={this.state.loading}
                            disabled={(!this.state.file) || this.state.alert.visible}
                        >
                            <Icon icon='trashcan' size='small' className={"em-u-margin-right-half"}></Icon>
                            Remove File
                        </Button>
                    </ButtonGroup>
                }
            >
                <CSVGeneratorButton
                    label="Download Template"
                    columns={this.state.templateColumns ?? []}
                    fileName={`Template-AWS-Rule-${this.props.direction?.name}.csv`}
                    data={[]}
                />
                <FileUpload
                    key={this.state.fileKey}
                    multiple={false}
                    onChange={this.handleChange}
                />
                {this.state.alert.visible &&
                    <Alert
                        variant="error"
                        body={this.state.alert.text}
                        onClose={() => {
                            this.setState({
                                alert: {
                                    visible: false,
                                }
                            });
                        }}
                    >
                        {this.state.alert.text}
                    </Alert>}
            </StandardModal>
        );
    }
}